import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  GOOGLE_REVIEWS_LIST_DEFAULT_LIMIT,
  type EffectiveGoogleSummary,
  type GoogleExportSummary,
  type GoogleImportPreview,
  type GoogleImportRequest,
  type GoogleReviewAdminItem,
  type GoogleReviewOrigin,
} from "@ckgo2/shared";
import { api, type ApiResponse } from "@/api/client";

/**
 * Jediné místo s cestami, tvary odpovědí a klíči dotazů tras Google recenzí
 * (`/api/admin/google-reviews`), ať si stránky nepíší URL ani klíče každá po svém.
 *
 * Klíče skládá výhradně `googleReviewsKeys`: jedno `invalidateQueries({ queryKey:
 * googleReviewsKeys.all })` obnoví seznam i souhrn a `useEditPage` s `listKey:
 * GOOGLE_REVIEWS_QUERY_KEY` je po uložení zahodí. Souhrn čtou panel nahrání i blok
 * v Nastavení přes `useGoogleReviewSummary`, takže sdílí jednu keš. Přečtený export ani
 * požadavek importu do klíče dotazu nikdy nejdou (osobní údaje by zůstaly v keši), proto
 * jsou náhled a potvrzení mutace.
 */
export const GOOGLE_REVIEWS_QUERY_KEY = "google-reviews";

export const googleReviewsKeys = {
  all: [GOOGLE_REVIEWS_QUERY_KEY],
  list: (params: GoogleReviewListParams) => [GOOGLE_REVIEWS_QUERY_KEY, "list", params] as const,
  summary: [GOOGLE_REVIEWS_QUERY_KEY, "summary"],
} as const;

/** Hodnota filtru destinace pro recenze bez destinace (jinak id destinace). */
export const GOOGLE_REVIEW_DESTINATION_NONE = "none";

const BASE = "/api/admin/google-reviews";

interface GoogleReviewListParams {
  page: number;
  origin: GoogleReviewOrigin | null;
  /** Id destinace, nebo `GOOGLE_REVIEW_DESTINATION_NONE` = recenze bez destinace. */
  destinationId: string | null;
}

/** Seznam (bez `content`); počet stránek bere UI z `meta.totalPages`, server zná použitý limit. */
export interface GoogleReviewListResponse {
  data: GoogleReviewAdminItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
  success: true;
}

interface GoogleReviewSummaryResponse {
  exported: GoogleExportSummary | null;
  effective: EffectiveGoogleSummary;
}

/**
 * `page` a `limit` jdou vždy (velikost stránky = výchozí limit Kontraktu), `origin`
 * a `destinationId` jen při zvoleném filtru. Filtr se v paměti nikdy nepočítá.
 */
function listGoogleReviews({
  page,
  origin,
  destinationId,
}: GoogleReviewListParams): Promise<GoogleReviewListResponse> {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(GOOGLE_REVIEWS_LIST_DEFAULT_LIMIT),
  });
  if (origin) query.set("origin", origin);
  if (destinationId) query.set("destinationId", destinationId);
  return api.get<GoogleReviewListResponse>(`${BASE}?${query.toString()}`);
}

export async function getGoogleReviewSummary(): Promise<GoogleReviewSummaryResponse> {
  const res = await api.get<ApiResponse<GoogleReviewSummaryResponse>>(`${BASE}/summary`);
  return res.data;
}

/** Stránka seznamu; při změně filtru nebo stránky zůstává vidět předchozí stránka. */
export function useGoogleReviewsList(params: GoogleReviewListParams) {
  return useQuery({
    queryKey: googleReviewsKeys.list(params),
    queryFn: () => listGoogleReviews(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Efektivní a exportovaný souhrn; jedna keš pro panel nahrání i Nastavení → Obsah webu.
 * Efektivní souhrn závisí i na ručních číslech ze záložky Společnost, jejichž uložení tento
 * klíč nezná; proto se při každém připojení čte znovu (globální `staleTime` 5 min by jinak
 * po přepnutí záložky ukázal číslo z doby před uložením).
 */
export function useGoogleReviewSummary() {
  return useQuery({
    queryKey: googleReviewsKeys.summary,
    queryFn: getGoogleReviewSummary,
    refetchOnMount: "always",
  });
}

export async function previewGoogleImport(
  request: GoogleImportRequest,
): Promise<GoogleImportPreview> {
  const res = await api.post<ApiResponse<GoogleImportPreview>>(`${BASE}/import/preview`, request);
  return res.data;
}

export async function confirmGoogleImport(
  request: GoogleImportRequest,
): Promise<GoogleImportPreview> {
  const res = await api.post<ApiResponse<GoogleImportPreview>>(`${BASE}/import/confirm`, request);
  return res.data;
}

/** Návrh destinace z textu (F2.7); nic nezapisuje. */
export async function suggestGoogleReviewDestination(text: string): Promise<string | null> {
  const res = await api.post<ApiResponse<{ destinationId: string | null }>>(
    `${BASE}/destination-suggestion`,
    { text },
  );
  return res.data.destinationId;
}

/** Oprava destinace: `null` = bez destinace; v obou případech zamkne destinaci proti importu. */
export function setGoogleReviewDestination(
  id: string,
  destinationId: string | null,
): Promise<unknown> {
  return api.patch(`${BASE}/${encodeURIComponent(id)}/destination`, { destinationId });
}

export function removeGoogleReviewPermanently(id: string): Promise<unknown> {
  return api.post(`${BASE}/${encodeURIComponent(id)}/remove-permanently`, {});
}

/** Smazání jde jen u ruční recenze (u exportované vrátí server 409). */
export function deleteManualGoogleReview(id: string): Promise<unknown> {
  return api.delete(`${BASE}/${encodeURIComponent(id)}`);
}
